import { Link } from 'react-router-dom'
import { ExplicacaoDoIndicador } from '../ExplicacaoDoIndicador'
import { LeiturasDoEnquadramento } from './LeiturasDoEnquadramento'
import { cn } from '../../../lib/cn'
import { AJUDA_DO_INDICADOR, TEXTOS } from '../../../lib/glossario'
import type { IndicadorNaTela } from '../../../types/chamado'
import { ComposicaoDoNumerador } from './ComposicaoDoNumerador'
import { OcorrenciasDoIad } from './OcorrenciasDoIad'
import { ParcelasDoIie } from './ParcelasDoIie'
import { ParcelasDoTis } from './ParcelasDoTis'
import { fracaoPorExtenso, valorPorExtenso } from './indicadores'

/**
 * Um indicador do ANEXO VIII, com tudo que é preciso para conferi-lo.
 *
 * Quatro coisas ficam lado a lado, e nenhuma é enfeite: o valor, **o denominador ao lado dele**, a
 * meta do contrato e a faixa de desconto em que o valor caiu. O TRP assinado escreve
 * "IAP = 25/36 = 69,44%" — um indicador sem denominador exposto é um número que ninguém consegue
 * contestar, e é sobre ele que se assina uma glosa.
 *
 * **Indicador não apurado nunca vira zero.** Zero é o valor de quem cumpriu a meta; num indicador
 * cuja meta é "quanto maior melhor" — ou "quanto menor melhor", que é o caso do IAD —, zero de
 * desconto lê-se como conformidade perfeita. Zeros silenciosos fariam a tela dizer "sem desconto"
 * sobre parte do anexo que ninguém olhou. Sem número, o cartão mostra o motivo que o servidor
 * mandou, em pt-BR, no lugar do valor.
 *
 * **Nem todo indicador apurado tem nota.** O TIS soma um desconto por incidente e não produz
 * percentual nenhum: para ele, `valor` é nulo com `apurado` verdadeiro, e o lugar do número grande é
 * ocupado pela soma, com a lista de incidentes logo abaixo. Escrever a soma como se fosse nota faria
 * "10%" parecer uma medição de qualidade quando é o teto do contrato.
 *
 * @param props o indicador apurado ou não
 * @returns o cartão do indicador
 */
export function CartaoDeIndicador({ indicador, competencia }: { indicador: IndicadorNaTela; competencia?: string }) {
  const ajuda = AJUDA_DO_INDICADOR[indicador.codigo]
  const valor = valorPorExtenso(indicador.codigo, indicador.valor)
  const fracao = fracaoPorExtenso(indicador)

  return (
    <li className={cn('cartao flex h-full flex-col p-5', !indicador.apurado && 'border-dashed bg-slate-50')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-900">
            {indicador.rotulo} <span className="text-sm font-normal text-slate-500">({indicador.codigo})</span>{' '}
            <ExplicacaoDoIndicador codigo={indicador.codigo} />
          </h3>
          <p className="mt-0.5 max-w-prose text-sm text-slate-600">{indicador.pergunta}</p>
        </div>
        <SeloDeDesconto indicador={indicador} />
      </div>

      {!indicador.apurado && (
        <NaoApurado motivo={indicador.motivoDaNaoApuracao} codigo={indicador.codigo} competencia={competencia} />
      )}

      {indicador.apurado && indicador.parcelas && <SomaDosDescontos parcelas={indicador.parcelas} />}

      {/* O IIE também é apurado sem nota: ele consolida quatro medições num redutor. O número
          grande é esse redutor, e o que ele consolidou vem logo abaixo. */}
      {indicador.apurado && indicador.consolidacao && (
        <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-3xl font-bold tabular-nums text-slate-900">
            {indicador.consolidacao.redutorPercentual === 0
              ? TEXTOS.iie.dentroDaMeta
              : `−${indicador.consolidacao.redutorPercentual}%`}
          </span>
          <span className="text-sm text-slate-700">
            {TEXTOS.fiscalizacao.denominador}{' '}
            <span className="font-medium tabular-nums">
              {indicador.consolidacao.parcelas.length} de 4 medições
            </span>
          </span>
        </div>
      )}

      {indicador.apurado && !indicador.parcelas && !indicador.consolidacao && (
        <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-3xl font-bold tabular-nums text-slate-900">{valor}</span>
          {/* O denominador ao lado do valor, e não numa linha de rodapé: é ele que torna o número
              conferível, e informação que se lê depois já foi lida como número solto. */}
          {fracao && (
            <span className="text-sm text-slate-700">
              {TEXTOS.fiscalizacao.denominador} <span className="font-medium tabular-nums">{fracao}</span>
            </span>
          )}
        </div>
      )}

      <dl className="mt-4 space-y-1 text-xs text-slate-600">
        {ajuda && (
          <>
            <Linha rotulo="Meta do contrato" valor={ajuda.meta} />
            {/* Meta e "sem desconto" são números diferentes no IAE — a meta é 75%, e a tabela de
                desconto só zera a partir de 90%. Mostrar só a meta ao lado de um desconto de 3%
                pareceria erro do sistema; mostrar os dois mostra que é o contrato que diz isso. */}
            <Linha rotulo="Sem desconto a partir de" valor={ajuda.semDesconto} />
            <Linha rotulo="Como se calcula" valor={ajuda.formula} />
            <Linha rotulo="Onde conferir" valor={ajuda.ondeNoTr} />
          </>
        )}
        {/* O número que decidiu o desconto, quando ele não é o que está escrito em cima. A tabela
            do contrato é feita de degraus, e sem esta linha a tela mostrava "89,86%" ao lado de
            "faixa: igual ou acima de 90%" sem explicar como um virou o outro. */}
        {indicador.valorEnquadrado != null && indicador.valorEnquadrado !== indicador.valor && (
          <Linha
            rotulo={TEXTOS.enquadramento.enquadradoComo}
            valor={valorPorExtenso(indicador.codigo, indicador.valorEnquadrado) ?? ''}
          />
        )}
        {indicador.faixaDoTr && <Linha rotulo="Faixa atingida" valor={indicador.faixaDoTr} />}
      </dl>

      <LeiturasDoEnquadramento indicador={indicador} />

      {indicador.parcelas && <ParcelasDoTis parcelas={indicador.parcelas} />}

      <OcorrenciasDoIad ocorrencias={indicador.ocorrencias} />

      {indicador.consolidacao && <ParcelasDoIie consolidacao={indicador.consolidacao} />}

      <div className="mt-auto pt-3">
        <ComposicaoDoNumerador parcelas={indicador.composicao} />
      </div>
    </li>
  )
}

/**
 * O número grande de um indicador que se apura somando, e não medindo.
 *
 * A soma bruta fica ao lado do que entra na nota **sempre que os dois diferem**. Mostrar só "10%"
 * quando os atrasos somaram 28% esconde exatamente o que quem assina precisa saber: que o teto do
 * contrato está segurando o desconto, e que o mês foi pior do que o número diz.
 */
function SomaDosDescontos({ parcelas }: { parcelas: NonNullable<IndicadorNaTela['parcelas']> }) {
  return (
    <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="text-3xl font-bold tabular-nums text-slate-900">−{parcelas.redutorDoIndicador}%</span>
      {parcelas.tetoAlcancado && (
        <span className="text-sm text-slate-700">
          {TEXTOS.fiscalizacao.denominador}{' '}
          <span className="font-medium tabular-nums">
            {parcelas.somaBruta}% somados, limitados a {parcelas.tetoDoIndicador}%
          </span>
        </span>
      )}
    </div>
  )
}

/** Uma linha da ficha do indicador. `<dl>` para o leitor de tela ligar rótulo e valor. */
function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex flex-wrap gap-x-1.5">
      <dt className="text-slate-500">{rotulo}:</dt>
      <dd className="font-medium text-slate-800">{valor}</dd>
    </div>
  )
}

/**
 * O que o indicador custa na nota do mês.
 *
 * "0%" só aparece em indicador APURADO — ali ele é uma afirmação: mediu-se e não houve desconto. No
 * não apurado, o selo diz "não apurado", porque o mesmo "0%" nos dois lugares apagaria a diferença
 * entre cumprir a meta e não ter sido medido.
 */
function SeloDeDesconto({ indicador }: { indicador: IndicadorNaTela }) {
  if (!indicador.apurado || indicador.redutorPercentual == null) {
    return (
      <span className="whitespace-nowrap rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700">
        {TEXTOS.fiscalizacao.naoApurado}
      </span>
    )
  }
  const semDesconto = indicador.redutorPercentual === 0
  return (
    <span
      className={cn(
        'whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset',
        semDesconto ? 'bg-green-50 text-green-900 ring-green-200' : 'bg-red-50 text-red-900 ring-red-200',
      )}
    >
      {semDesconto ? 'Sem desconto' : `−${indicador.redutorPercentual}% na nota`}
    </span>
  )
}

/** O motivo no lugar do número. O texto vem do servidor: é lá que a razão é conhecida. */
/**
 * "Não apurado" com a saída: o IAD leva à conferência das ocorrências, e o calendário diz quem
 * confere. Dizer o que falta sem levar até o ato deixava o fiscal parado no cartão.
 */
function NaoApurado({ motivo, codigo, competencia }: { motivo: string | null; codigo: string; competencia?: string }) {
  const mencionaCalendario = /calend[áa]rio|dia[s]? n[ãa]o [úu]t/i.test(motivo ?? '')
  return (
    <div className="mt-4 space-y-1">
      <p className="text-lg font-semibold text-slate-700">{TEXTOS.fiscalizacao.naoApurado}</p>
      {motivo && <p className="max-w-prose text-sm text-slate-700">{motivo}</p>}
      <p className="max-w-prose text-xs text-slate-600">{TEXTOS.fiscalizacao.naoApuradoAjuda}</p>
      {codigo === 'IAD' && competencia && (
        <Link
          to={`/iad?competencia=${encodeURIComponent(competencia)}`}
          className="inline-block text-sm font-medium text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
        >
          {TEXTOS.fiscalizacao.saida.iad}
        </Link>
      )}
      {codigo === 'TIS' && mencionaCalendario && (
        <p className="max-w-prose text-xs text-slate-600">{TEXTOS.fiscalizacao.saida.calendario}</p>
      )}
    </div>
  )
}
